import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

/** A single step descriptor for the {@link StepperComponent}. */
export interface StepperStepI {
  /** Visible label for the step. */
  label: string;
  /** Optional one-line description rendered beneath the label. */
  description?: string;
  /** When true the step cannot be navigated to (rendered muted, non-interactive). */
  disabled?: boolean;
}

interface StepperStepView extends StepperStepI {
  number: number;
  state: 'complete' | 'active' | 'pending';
}

/**
 * Stepper (#1142) — a presentational multi-step indicator for wizard-style flows
 * (the assessment wizard #1156). Generic and standalone: it takes the steps + the
 * current 1-based index and renders complete / active / pending states.
 *
 * Distinct from the booking-flow `StepIndicatorComponent`, which is specialised to
 * that flow. This primitive additionally emits a `stepSelected` event so a host
 * wizard can allow jumping back to a completed step.
 *
 * Accessibility:
 *  - `role="list"` with one `role="listitem"` per step.
 *  - `aria-current="step"` on the active step.
 *  - Each step exposes an accessible name "Step N of M: {label}".
 *  - Interactive steps are real `<button>`s so they are keyboard-focusable.
 *
 * Token-driven styling only (amber active, deep-ocean complete, muted pending).
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-stepper',
  standalone: true,
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
})
export class StepperComponent {
  readonly steps = input.required<StepperStepI[]>();
  /** 1-based index of the active step. */
  readonly currentStep = input.required<number>();
  /** When true, completed steps become clickable to navigate backward. */
  readonly clickable = input<boolean>(false);

  /** Emits the 1-based index of a step the user activated. */
  readonly stepSelected = output<number>();

  protected readonly stepViews = computed<StepperStepView[]>(() =>
    this.steps().map((step, index) => {
      const number = index + 1;
      const current = this.currentStep();
      const state: StepperStepView['state'] = number < current ? 'complete' : number === current ? 'active' : 'pending';
      return { ...step, number, state };
    }),
  );

  protected stepAriaLabel(view: StepperStepView): string {
    return `Step ${view.number} of ${this.steps().length}: ${view.label}`;
  }

  /** A completed, non-disabled step is navigable when the stepper is clickable. */
  protected isInteractive(view: StepperStepView): boolean {
    return this.clickable() && !view.disabled && view.state === 'complete';
  }

  protected handleSelect(view: StepperStepView): void {
    if (!this.isInteractive(view)) return;
    this.stepSelected.emit(view.number);
  }
}
